/**
 * Helpers extracted from the URL-params parse/hydrate `useEffect` in
 * SearchResults/index.jsx (TD-2377 — SonarQube cognitive-complexity cleanup,
 * Phase 3).
 *
 * That effect walked the SAME nested (searchType × searchCategory) decision
 * matrix THREE times — once to parse the criteria fields out of the query
 * string (with per-type "required" validation), once to hydrate the matching
 * criteria field bag on the store, and once to compute the `initial` blank-field
 * template used when a param is absent. Three copies of the same four-way tree
 * are what pushed the effect to cognitive complexity 54 (threshold 15).
 *
 * The matrix now lives here as a flat list of descriptors keyed by
 * (searchType, searchCategory). Each descriptor carries the field-constant
 * object for that combination plus the string key of the store bag it hydrates,
 * so `resolveUrlDescriptor` + the three pure helpers below replace all three
 * copies. A descriptor references the `state` bag by string key (bracket
 * access) rather than closing over the store, mirroring the sibling
 * `searchDispatchHelpers.js` / `getResultsHelpers.js` precedents from Phases 1
 * and 2 — the SearchResults store has no unit harness, so the extracted logic
 * is a pure function of a plain `state` object and directly unit-testable.
 */
import { runInAction } from 'mobx';
import { UI_OPTIONS } from '../../../utils/constants/uiOptions';
import { UI_OPTION_KEYS } from '../../../utils/constants/uiOptionKeys';
import {
  SEARCH_FIELDS,
  SEARCH_FIELDS_PORTFOLIO_INVESTMENT_CRITERIA,
  PROFILER_SEARCH_FIELDS,
  PROFILER_INDUSTRY_CODES_SEARCH_FIELDS,
  isEmptyValue
} from '../../../utils/constants/fields';
import { mapFieldsToState } from '../../../utils/utils';

const SEARCH_TYPE = UI_OPTIONS[UI_OPTION_KEYS.SEARCH_TYPE];
const FUND_CATEGORY = UI_OPTIONS[UI_OPTION_KEYS.FUND_SEARCH_CATEGORY];
const PORTFOLIO_CATEGORY = UI_OPTIONS[UI_OPTION_KEYS.PORTFOLIO_SEARCH_CATEGORY];
const PROFILER_CATEGORY = UI_OPTIONS[UI_OPTION_KEYS.PROFILER_SEARCH_CATEGORY];

/**
 * One entry per supported (searchType × searchCategory) combination the URL can
 * hydrate.
 *
 * - `fields` — the field-config object parsed out of the query string and used
 *   as the `mapFieldsToState` template for absent params.
 * - `criteriaFieldsKey` — string key of the store bag hydrated with the parsed
 *   values.
 * - `allowSingleRangeGuard` — the Profiler branches skip the "required" error
 *   for fields flagged `ALLOW_SINGLE_RANGE`; Funds/Portfolio do not. Preserves
 *   the two different required-emptiness conditions from the original effect.
 * - `searchTypeFieldName` — Profiler branches additionally stamp the resolved
 *   search type back onto the criteria bag under this field name; undefined for
 *   Funds/Portfolio, which don't.
 * - `industryCodesFieldName` — Profiler/Industries also mirrors the parsed
 *   industry-codes list to `state.profilerIndustriesListFromURL`; undefined
 *   elsewhere.
 *
 * Order is irrelevant — the (type, category) pairs are disjoint.
 */
export const URL_PARAM_DESCRIPTORS = [
  {
    searchType: SEARCH_TYPE.Profiler,
    searchCategory: PROFILER_CATEGORY['Characteristics'],
    fields: PROFILER_SEARCH_FIELDS,
    criteriaFieldsKey: 'profilerCompanyCriteriaFields',
    allowSingleRangeGuard: true,
    searchTypeFieldName: PROFILER_SEARCH_FIELDS.SEARCH_TYPE.NAME,
    industryCodesFieldName: undefined
  },
  {
    searchType: SEARCH_TYPE.Profiler,
    searchCategory: PROFILER_CATEGORY['Industries'],
    fields: PROFILER_INDUSTRY_CODES_SEARCH_FIELDS,
    criteriaFieldsKey: 'profilerIndustryCodesFields',
    allowSingleRangeGuard: true,
    searchTypeFieldName: PROFILER_INDUSTRY_CODES_SEARCH_FIELDS.SEARCH_TYPE.NAME,
    industryCodesFieldName: PROFILER_INDUSTRY_CODES_SEARCH_FIELDS.INDUSTRY_CODES.NAME
  },
  {
    searchType: SEARCH_TYPE.Funds,
    searchCategory: FUND_CATEGORY['Investment criteria'],
    fields: SEARCH_FIELDS,
    criteriaFieldsKey: 'fundsInvestmentCriteriaFields',
    allowSingleRangeGuard: false,
    searchTypeFieldName: undefined,
    industryCodesFieldName: undefined
  },
  {
    searchType: SEARCH_TYPE['Portfolio Co.'],
    searchCategory: PORTFOLIO_CATEGORY['Investment criteria'],
    fields: SEARCH_FIELDS_PORTFOLIO_INVESTMENT_CRITERIA,
    criteriaFieldsKey: 'portfoliosInvestmentCriteriaFields',
    allowSingleRangeGuard: false,
    searchTypeFieldName: undefined,
    industryCodesFieldName: undefined
  }
];

/**
 * Parse `searchType` + `searchCategory` out of the query string and validate
 * them against the store's currently-enabled categories. Returns the parsed
 * values plus `hasInvalidParam` (true when the type isn't a known search type or
 * the category isn't an enabled one for that type). Extracted verbatim from the
 * URL-params effect — the `allowedCategories` map is built from the same three
 * `state.*Categories` bags (disabled entries filtered out), and a malformed
 * `searchType` (or an unknown type indexing `allowedCategories`) throws and is
 * caught into `hasInvalidParam`, exactly as before.
 */
export const resolveSearchTypeAndCategory = (state, urlParams) => {
  const allowedCategories = {
    [SEARCH_TYPE.Funds]: state.fundsCategories
      .filter(({ disabled }) => !disabled)
      .map(({ value }) => value),
    [SEARCH_TYPE['Portfolio Co.']]: state.portfoliosCategories
      .filter(({ disabled }) => !disabled)
      .map(({ value }) => value),
    [SEARCH_TYPE.Profiler]: state.profilerCategories
      .filter(({ disabled }) => !disabled)
      .map(({ value }) => value)
  };

  let searchType = null;
  let searchCategory = null;
  let hasInvalidParam = false;
  try {
    searchType = JSON.parse(decodeURIComponent(urlParams.get('searchType')))[0];
    if (!Object.values(SEARCH_TYPE).includes(searchType)) {
      hasInvalidParam = true;
    }
    searchCategory = decodeURIComponent(urlParams.get('searchCategory'));
    if (!allowedCategories[searchType].includes(searchCategory)) {
      hasInvalidParam = true;
    }
  } catch {
    hasInvalidParam = true;
  }
  return { searchType, searchCategory, hasInvalidParam };
};

/**
 * Resolve a (searchType, searchCategory) pair to its URL descriptor, or null
 * when the combination isn't one the URL can hydrate. Pure. A null result is
 * treated by the caller as "no fields to parse/hydrate" — identical to the
 * original effect's unmatched inner branches, which left `searchFields` null.
 */
export const resolveUrlDescriptor = (searchType, searchCategory) =>
  URL_PARAM_DESCRIPTORS.find(
    (descriptor) =>
      descriptor.searchType === searchType && descriptor.searchCategory === searchCategory
  ) ?? null;

/**
 * Parse the descriptor's criteria fields out of the query string. Returns the
 * parsed `searchFields` object and whether any REQUIRED field came back empty
 * (`hasInvalidParam`). May throw `SyntaxError` on malformed JSON — the caller
 * keeps the original try/catch that maps that to `hasInvalidParam`.
 *
 * A param that is absent (a link trimmed or edited outside the app, or saved
 * before the field existed) or explicitly `null` gets the field's blank value,
 * never null — downstream code reads every value as set.
 */
export const parseSearchFieldsFromUrl = (descriptor, urlParams) => {
  let hasInvalidParam = false;
  const blank = mapFieldsToState(descriptor.fields);
  const searchFields = Object.values(descriptor.fields).reduce((fields, f) => {
    const raw = urlParams.get(f.NAME);
    fields[f.NAME] = (raw === null ? null : JSON.parse(decodeURIComponent(raw))) ?? blank[f.NAME];
    const missingRequired = descriptor.allowSingleRangeGuard
      ? f.REQUIRED && !f.ALLOW_SINGLE_RANGE && isEmptyValue(fields[f.NAME])
      : f.REQUIRED && isEmptyValue(fields[f.NAME]);
    if (missingRequired) {
      hasInvalidParam = true;
    }
    return fields;
  }, {});
  return { searchFields, hasInvalidParam };
};

/**
 * Hydrate the descriptor's criteria field bag on `state` from the parsed
 * `searchFields`, falling back to the blank-field template for absent values.
 * Also mirrors the industry-codes list and stamps the search type back onto the
 * bag for the Profiler branches. Wraps its writes in `runInAction` exactly like
 * the original effect.
 */
export const hydrateSearchFields = (state, descriptor, searchFields) => {
  runInAction(() => {
    const initial = mapFieldsToState(descriptor.fields);
    Object.entries(searchFields).forEach(([fieldName, fieldValue]) => {
      state[descriptor.criteriaFieldsKey][fieldName] = fieldValue || initial[fieldName];
      if (descriptor.industryCodesFieldName && fieldName === descriptor.industryCodesFieldName) {
        state.profilerIndustriesListFromURL = (fieldValue || initial[fieldName]).slice();
      }
    });
    if (descriptor.searchTypeFieldName) {
      state[descriptor.criteriaFieldsKey][descriptor.searchTypeFieldName] = [descriptor.searchType];
    }
  });
};

/**
 * Build the `queryFields` object compared against the store's `currentSearch`
 * to decide whether to (re)issue the search. Mirrors the original: each parsed
 * value falls back to the descriptor's blank-field template, and a null
 * `searchFields` (no matching descriptor) yields `{}` — which `compareFields`
 * treats as "no change", so no search fires.
 */
export const buildQueryFields = (descriptor, searchFields) => {
  if (!searchFields || !descriptor) {
    return {};
  }
  const initial = mapFieldsToState(descriptor.fields);
  return Object.fromEntries(
    Object.entries(searchFields).map(([fieldName, fieldValue]) => [
      fieldName,
      fieldValue || initial[fieldName]
    ])
  );
};
